import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { sessionsEndpoints } from '../../../endpoints/sessions';
import {
  CELL_RENDERING,
  CellRendering,
  ICatalogResponse,
  ITableResponse,
  SYSTEM_PRESET_ID,
  TABLE_MODE,
  TableMode,
  UNIT_MODE,
  UnitMode,
} from '../../../models/sessions';

export interface IPeriod {
  start: string; // YYYY-MM-DD
  end: string;
}

export interface ITableFilters {
  roles?: string[];
  sessionTypes?: string[];
  customGroupId?: string;
  /** intensity buckets: low/medium/high */
  intensityBuckets?: string[];
  /** range RPE 0-10 */
  rpeRange?: { min: number; max: number };
  /** percentile filter: lt_p25/p25_p75/gt_p75/outlier */
  percentileFilter?: string;
  /** baseline filter: above/in_line/below */
  baselineFilter?: string[];
  /** activity category filter */
  activityCategoryFilter?: string[];
}

export const startOfThisWeek = (): string =>
  dayjs().startOf('week').add(1, 'day').format('YYYY-MM-DD'); // lunedì

export const endOfThisWeek = (): string =>
  dayjs().startOf('week').add(7, 'day').format('YYYY-MM-DD'); // domenica

/** Periodo default: ultimi 31 giorni (today incluso). Usato come default della pagina
 *  per garantire dati visibili anche se il seed copre solo i 30 giorni precedenti. */
export const defaultPeriodStart = (): string =>
  dayjs().subtract(30, 'day').format('YYYY-MM-DD');

export const defaultPeriodEnd = (): string =>
  dayjs().format('YYYY-MM-DD');

/**
 * Hook principale Tabella Carichi.
 * Gestisce stato locale (mode, unitMode, presetId, period, kpi, cellRendering, filters).
 */
export type Subview = 'calendar' | 'table' | 'report';

export function useCarichiTable(teamId: string | undefined) {
  const [subview, setSubview] = useState<Subview>('table');
  const [mode, setMode] = useState<TableMode>(TABLE_MODE.AGGREGATE);
  const [unitMode, setUnitMode] = useState<UnitMode>(UNIT_MODE.ABSOLUTE);
  const [presetId, setPresetId] = useState<string>(
    SYSTEM_PRESET_ID.KAMA_STANDARD,
  );
  const [period, setPeriod] = useState<IPeriod>({
    start: defaultPeriodStart(),
    end: defaultPeriodEnd(),
  });
  const [kpiMetric, setKpiMetric] = useState<string>('td');
  const [cellRendering, setCellRendering] = useState<CellRendering>(
    CELL_RENDERING.VALUE,
  );
  const [filters, setFilters] = useState<ITableFilters>({});

  const catalogQuery = useQuery<ICatalogResponse>({
    queryKey: ['sessions-catalog'],
    queryFn: async () => (await sessionsEndpoints.catalog()).data,
    staleTime: 60 * 60 * 1000, // 1 ora — catalog statico
  });

  const tableQuery = useQuery<ITableResponse>({
    enabled: !!teamId,
    queryKey: [
      'sessions-table',
      teamId,
      mode,
      unitMode,
      presetId,
      period,
      kpiMetric,
      filters,
    ],
    queryFn: async () => {
      const res = await sessionsEndpoints.getTeamTable(teamId!, {
        mode,
        unitMode,
        presetId,
        periodStart: period.start,
        periodEnd: period.end,
        kpiMetric: mode === TABLE_MODE.WEEK_MATRIX ? kpiMetric : undefined,
        roles: filters.roles,
        sessionTypes: filters.sessionTypes as any,
        customGroupId: filters.customGroupId,
        intensityBuckets: filters.intensityBuckets,
        rpeRange:
          filters.rpeRange != null
            ? `${filters.rpeRange.min},${filters.rpeRange.max}`
            : undefined,
        percentileFilter: filters.percentileFilter,
        baselineFilter: filters.baselineFilter?.join(','),
        activityCategoryFilter: filters.activityCategoryFilter?.join(','),
      });
      return res.data;
    },
  });

  const metricsByGroup = useMemo(() => {
    const all = catalogQuery.data?.metrics ?? [];
    const map: Record<string, typeof all> = {};
    for (const m of all) {
      if (!map[m.group]) map[m.group] = [];
      map[m.group].push(m);
    }
    return map;
  }, [catalogQuery.data]);

  const activeColumns = tableQuery.data?.meta.columns ?? [];

  return {
    // stato
    subview,
    setSubview,
    mode,
    setMode,
    unitMode,
    setUnitMode,
    presetId,
    setPresetId,
    period,
    setPeriod,
    kpiMetric,
    setKpiMetric,
    cellRendering,
    setCellRendering,
    filters,
    setFilters,
    // data
    catalog: catalogQuery.data,
    metricsByGroup,
    activeColumns,
    table: tableQuery.data,
    // loading/error
    isLoading: catalogQuery.isLoading || tableQuery.isLoading,
    isError: catalogQuery.isError || tableQuery.isError,
    error: catalogQuery.error || tableQuery.error,
    // refetch
    refetch: tableQuery.refetch,
  };
}
